import React from "react";
import { DatePicker, ConfigProvider, Popover, Modal, Calendar, Badge } from "antd";
import esES from "antd/es/locale/es_ES";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
  getMandatoryDays,
  addMandatoryDay,
  createMandatoryDay,
  createMultipleMandatoryDays,
  removeMandatoryDay,
  removePartnerFromDate,
  bulkRemovePartners,
  bulkCreateMandatoryDays,
  type MandatoryDayData,
  type PartnerBonus,
} from "../../services/mandatory-days.service.ts";
import { autocompletePartners, readPartnersByBranch } from "../../services/partner.service.ts";
import { readBranches } from "../../services/branch.service.ts";
import { notification } from "../../utils/Notifications.tsx";

dayjs.locale("es");

interface EditMandatoryDaysProps {
  closeFn: () => void;
  setLoading: (val: boolean) => void;
  showAssignmentControls?: boolean;
}

interface Partner {
  id: number;
  person: {
    firstName: string;
    lastName: string;
    secondLastName?: string;
  };
}

interface Branch {
  id: number;
  name: string;
}

const EditMandatoryDays = ({
  closeFn,
  setLoading,
  showAssignmentControls = true,
}: EditMandatoryDaysProps) => {
  const [selectedDate, setSelectedDate] = React.useState(null as any);
  const [selectedRange, setSelectedRange] = React.useState<any>(null);
  const [mandatoryDays, setMandatoryDays] = React.useState(
    [] as MandatoryDayData[]
  );
  const [partners, setPartners] = React.useState([] as Partner[]);
  const [branches, setBranches] = React.useState([] as Branch[]);
  const [selectedBranch, setSelectedBranch] = React.useState<number | null>(null);
  const [selectedPartner, setSelectedPartner] = React.useState<number | null>(
    null
  );
  const [selectedPartners, setSelectedPartners] = React.useState<number[]>([]);
  const [selectAllMode, setSelectAllMode] = React.useState<boolean>(false);
  const [bonus, setBonus] = React.useState<string>("");
  const [searchPartner, setSearchPartner] = React.useState<string>("");
  const [filteredPartners, setFilteredPartners] = React.useState<Partner[]>([]);
  const [showPartnerDropdown, setShowPartnerDropdown] = React.useState(false);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const searchContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<Set<string>>(
    new Set()
  );
  const [showBulkDelete, setShowBulkDelete] = React.useState<boolean>(false);
  const [showPartnerDatesModal, setShowPartnerDatesModal] = React.useState<boolean>(false);
  const [selectedPartnerForDates, setSelectedPartnerForDates] = React.useState<{
    partnerId: number;
    partnerName: string;
    dates: Array<{ date: string; bonus: number }>;
  } | null>(null);
  const [calendarValue, setCalendarValue] = React.useState(dayjs());

  let timeoutId: any;

  const refreshDays = async () => {
    try {
      setLoading(true);
      let start: string | undefined;
      let end: string | undefined;
      if (selectedRange && selectedRange[0] && selectedRange[1]) {
        start = dayjs(selectedRange[0]).format("YYYY-MM-DD");
        end = dayjs(selectedRange[1]).format("YYYY-MM-DD");
      }
      const resp = await getMandatoryDays(start, end);
      setMandatoryDays(resp?.days || []);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const resp = await readBranches();
      setBranches(resp || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchPartners = async (searchTerm: string = "", branchId?: number) => {
    try {
      setSearchLoading(true);
      const resp = await autocompletePartners(searchTerm, branchId);
      setPartners(resp || []);
      setFilteredPartners(resp || []);
    } catch (error) {
      console.error("Error fetching partners:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleBranchChange = (branchId: number | null) => {
    setSelectedBranch(branchId);
    setSelectedPartner(null);
    setSelectedPartners([]);
    setSelectAllMode(false);
    setSearchPartner("");
    setFilteredPartners([]);
    fetchPartners("", branchId);
  };

  const handleSelectAllPartners = async () => {
    if (selectAllMode) {
      // Deseleccionar todos
      setSelectAllMode(false);
      setSelectedPartners([]);
      setSelectedPartner(null);
    } else {
      // Seleccionar todos los partners de la sucursal
      if (!selectedBranch) {
        notification("Error", "Debes seleccionar una sucursal primero", "warning");
        return;
      }
      
      try {
        setLoading(true);
        const allBranchPartners = await readPartnersByBranch(selectedBranch);
        const partnerIds = allBranchPartners.map(p => p.id);
        
        setSelectAllMode(true);
        setSelectedPartners(partnerIds);
        setSelectedPartner(null);
        
        notification(
          "Éxito", 
          `${partnerIds.length} colaboradores de la sucursal seleccionados`, 
          "success"
        );
      } catch (error) {
        notification("Error", "No se pudieron cargar los colaboradores de la sucursal", "danger");
      } finally {
        setLoading(false);
      }
    }
    // No cerrar el dropdown para mantener las opciones visibles
  };

  const handlePartnerToggle = (partnerId: number) => {
    if (selectedPartners.includes(partnerId)) {
      setSelectedPartners(selectedPartners.filter(id => id !== partnerId));
    } else {
      setSelectedPartners([...selectedPartners, partnerId]);
    }
    setSelectAllMode(false);
    setSelectedPartner(null);
    // No cerrar el dropdown para mantener las opciones visibles
  };

  const handlePartnerSearch = async (searchTerm: string) => {
    setSearchPartner(searchTerm);
    setShowPartnerDropdown(true);

    if (timeoutId) clearTimeout(timeoutId);

    timeoutId = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const data = await autocompletePartners(searchTerm, selectedBranch || undefined);
        setFilteredPartners(data);
      } catch (error) {
        console.error("Error searching partners:", error);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
  };

  React.useEffect(() => {
    fetchBranches();
    fetchPartners();
  }, []);

  React.useEffect(() => {
    refreshDays();
  }, [selectedRange?.[0], selectedRange?.[1]]);

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      const el = searchContainerRef.current as any;
      // Solo cerrar si el click es fuera del contenedor Y no es en un checkbox o botón del dropdown
      if (el && !el.contains(event.target) && 
          !event.target.closest('.partner-dropdown-item') && 
          !event.target.closest('input[type="checkbox"]') &&
          !event.target.closest('button')) {
        setShowPartnerDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAdd = async () => {
    const isRange = selectedRange && selectedRange[0] && selectedRange[1];
    if (!isRange) return;

    try {
      setLoading(true);

      // Generar array de fechas del rango seleccionado
      const [start, end] = selectedRange;
      let cursor = dayjs(start);
      const endDate = dayjs(end);
      const dates: string[] = [];
      
      while (!cursor.isAfter(endDate, "day")) {
        const yyyymmdd = cursor.format("YYYY-MM-DD");
        // Solo agregar fechas que no existan si no hay partner seleccionado
        const exists = mandatoryDays.find((day) => day.date === yyyymmdd);
        if (selectedPartner || !exists) {
          dates.push(yyyymmdd);
        }
        cursor = cursor.add(1, "day");
      }

      if (dates.length === 0) {
        notification(
          "Info",
          showAssignmentControls
            ? "No hay fechas nuevas para agregar"
            : "Todas las fechas del rango ya están bloqueadas.",
          "info"
        );
        return;
      }

      if (!showAssignmentControls) {
        const totalRangeDays = dayjs(end).diff(dayjs(start), "day") + 1;
        const alreadyBlocked = totalRangeDays - dates.length;
        if (alreadyBlocked > 0) {
          notification(
            "Info",
            `Se omiten ${alreadyBlocked} fecha(s) ya bloqueadas y se bloquean ${dates.length} nueva(s).`,
            "info"
          );
        }
      }

      // Determinar qué partners usar
      let partnersToProcess: number[] = [];
      if (selectAllMode && selectedPartners.length > 0) {
        partnersToProcess = selectedPartners;
      } else if (selectedPartner) {
        partnersToProcess = [selectedPartner];
      }

      // Si hay partners seleccionados, procesar todos en una sola llamada
      if (partnersToProcess.length > 0) {
        const partnerAssignments = partnersToProcess.map(partnerId => ({
          partnerId,
          bonus: bonus ? parseFloat(bonus) : undefined
        }));

        const resp = await bulkCreateMandatoryDays(dates, partnerAssignments);
        setMandatoryDays(resp?.days || []);
        
        notification(
          "Éxito",
          `${partnersToProcess.length} colaboradores agregados a ${dates.length} fechas`,
          "success"
        );
      } else {
        // Si no hay partners, solo crear las fechas
        const resp = await createMultipleMandatoryDays(dates);
        setMandatoryDays(resp?.days || []);
        
        notification(
          "Éxito",
          `${dates.length} fechas obligatorias creadas`,
          "success"
        );
      }

      // Reset parciales
      setSelectedPartner(null);
      setSelectedPartners([]);
      setSelectAllMode(false);
      setSearchPartner("");
      setBonus("");

    } catch (error) {
      notification("Error", "No se pudo procesar la solicitud", "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (date: string) => {
    try {
      setLoading(true);
      const resp = await removeMandatoryDay(date);
      setMandatoryDays(resp?.days || []);
      notification("Éxito", `Fecha eliminada`, "success");
    } catch (error) {
      notification("Error", "No se pudo eliminar la fecha", "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePartner = async (date: string, partnerId: number) => {
    try {
      setLoading(true);
      const resp = await removePartnerFromDate(date, partnerId);
      setMandatoryDays(resp?.days || []);
      notification("Éxito", `Colaborador eliminado`, "success");
    } catch (error) {
      notification("Error", "No se pudo eliminar el colaborador", "danger");
    } finally {
      setLoading(false);
    }
  };

  const isRegistered = (d: any) => {
    const yyyymmdd = dayjs(d).format("YYYY-MM-DD");
    return mandatoryDays.some((day) => day.date === yyyymmdd);
  };

  const getRegisteredDate = (d: any) => {
    const yyyymmdd = dayjs(d).format("YYYY-MM-DD");
    return mandatoryDays.find((day) => day.date === yyyymmdd);
  };

  const handleDateSelect = (date: any) => {
    setSelectedDate(date);

    if (isRegistered(date)) {
      return;
    }
  };

  const cellRender = (current: any, info: any) => {
    if (info?.type !== "date") return info?.originNode;
    const yyyymmdd = dayjs(current).format("YYYY-MM-DD");
    const marked = mandatoryDays.some((day) => day.date === yyyymmdd);
    const isToday = dayjs(current).isSame(dayjs(), "day");

    let style: React.CSSProperties = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto",
      width: 28,
      height: 28,
      borderRadius: "9999px",
      cursor: "pointer",
      backgroundColor: isToday ? "#e7f1ff" : undefined,
      border: isToday ? "2px dotted #0d6efd" : undefined,
      color: isToday ? "#0d6efd" : undefined,
    };

    if (marked) {
      style = {
        ...style,
        border: "2px solid #0d6efd",
        color: undefined,
      };
    }

    const circle = <div style={style}>{dayjs(current).date()}</div>;

    if (marked) {
      return (
        <Popover
          placement="top"
          trigger="click"
          getPopupContainer={(node: any) =>
            (node?.parentElement as HTMLElement) || document.body
          }
          content={
            <div style={{ minWidth: "200px" }}>
              <div className="mb-2">
                <strong>Fecha: {yyyymmdd}</strong>
              </div>
              <button
                className="btn btn-danger btn-sm w-100"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemove(yyyymmdd);
                }}
              >
                Eliminar fecha completa
              </button>
            </div>
          }
        >
          <div
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDateSelect(current);
            }}
            style={{ cursor: "pointer" }}
          >
            {circle}
          </div>
        </Popover>
      );
    }

    return (
      <div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDateSelect(current);
        }}
        style={{ cursor: "pointer" }}
      >
        {circle}
      </div>
    );
  };

  const getSelectedDatePartners = () => {
    if (!selectedDate) return [];
    const yyyymmdd = dayjs(selectedDate).format("YYYY-MM-DD");
    const registeredDate = mandatoryDays.find((day) => day.date === yyyymmdd);
    return registeredDate?.partners || [];
  };

  const selectedDatePartners = getSelectedDatePartners();

  const handlePartnerSelect = (partner: Partner) => {
    setSelectedPartner(partner.id);
    setSelectedPartners([]);
    setSelectAllMode(false);
    setSearchPartner(`${partner.person.firstName} ${partner.person.lastName}`);
    // No cerrar el dropdown para mantener las opciones visibles
  };

  const toggleRowSelection = (key: string) => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearSelection = () => setSelectedRowKeys(new Set());

  const parseRowKey = (key: string): { date: string; partnerId?: number } => {
    const [date, pid] = key.split("::");
    return { date, partnerId: pid ? Number(pid) : undefined };
  };

  const handleBulkDelete = async () => {
    try {
      setLoading(true);
      const keys = Array.from(selectedRowKeys);
      
      // Separar colaboradores de fechas individuales
      const partnerIds: number[] = [];
      const individualDeletes: Array<{ type: 'partner' | 'date', date?: string, partnerId?: number }> = [];
      
      for (const key of keys) {
        if (key.startsWith('partner::')) {
          // Agregar a la lista de colaboradores para eliminación masiva
          const partnerId = parseInt(key.replace('partner::', ''));
          partnerIds.push(partnerId);
        } else {
          // Agregar a la lista de eliminaciones individuales
          const { date, partnerId } = parseRowKey(key);
          if (partnerId) {
            individualDeletes.push({ type: 'partner', date, partnerId });
          } else {
            individualDeletes.push({ type: 'date', date });
          }
        }
      }
      
      // Eliminación masiva de colaboradores (una sola llamada)
      if (partnerIds.length > 0) {
        await bulkRemovePartners(partnerIds);
      }
      
      // Eliminaciones individuales (solo si hay fechas específicas)
      for (const deleteItem of individualDeletes) {
        if (deleteItem.type === 'partner' && deleteItem.date && deleteItem.partnerId) {
          await removePartnerFromDate(deleteItem.date, deleteItem.partnerId);
        } else if (deleteItem.type === 'date' && deleteItem.date) {
          await removeMandatoryDay(deleteItem.date);
        }
      }
      
      // Refrescar la lista
      const resp = await getMandatoryDays();
      setMandatoryDays(resp?.days || []);
      clearSelection();
      
      const totalDeleted = partnerIds.length + individualDeletes.length;
      notification("Éxito", `${totalDeleted} registros eliminados`, "success");
    } catch (error) {
      notification("Error", "No se pudo eliminar la selección", "danger");
    } finally {
      setShowBulkDelete(false);
      setLoading(false);
    }
  };

  const handleShowPartnerDates = (partnerId: number, partnerName: string) => {
    // Buscar todas las fechas donde está asignado este colaborador
    const partnerDates: Array<{ date: string; bonus: number }> = [];
    
    mandatoryDays.forEach(day => {
      const partnerAssignment = day.partners.find(p => p.partnerId === partnerId);
      if (partnerAssignment) {
        partnerDates.push({
          date: day.date,
          bonus: partnerAssignment.bonus
        });
      }
    });

    setSelectedPartnerForDates({
      partnerId,
      partnerName,
      dates: partnerDates
    });
    setShowPartnerDatesModal(true);
  };

  const getGroupedPartners = () => {
    const partnerMap = new Map<number, {
      partnerId: number;
      partnerName: string;
      dates: Array<{ date: string; bonus: number }>;
      totalBonus: number;
    }>();

    mandatoryDays.forEach(day => {
      day.partners.forEach(partnerBonus => {
        const existing = partnerMap.get(partnerBonus.partnerId);
        if (existing) {
          existing.dates.push({ date: day.date, bonus: partnerBonus.bonus });
          existing.totalBonus += partnerBonus.bonus;
        } else {
          partnerMap.set(partnerBonus.partnerId, {
            partnerId: partnerBonus.partnerId,
            partnerName: (partnerBonus as any).partnerName || 
              partners.find(p => p.id === partnerBonus.partnerId)?.person.firstName + ' ' + 
              partners.find(p => p.id === partnerBonus.partnerId)?.person.lastName || 
              'Colaborador',
            dates: [{ date: day.date, bonus: partnerBonus.bonus }],
            totalBonus: partnerBonus.bonus
          });
        }
      });
    });

    return Array.from(partnerMap.values()).sort((a, b) => a.partnerName.localeCompare(b.partnerName));
  };

  // Render ligero para RangePicker que conserva estilos de selección/hover del rango
  const rangeDateRender = (current: any) => {
    const yyyymmdd = dayjs(current).format("YYYY-MM-DD");
    const marked = mandatoryDays.some((day) => day.date === yyyymmdd);
    return (
      <div className="ant-picker-cell-inner" style={{ position: "relative" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            borderRadius: 9999,
            border: marked ? "2px solid #0d6efd" : "none",
          }}
        >
          {dayjs(current).date()}
        </span>
      </div>
    );
  };

  const selectedRangeLabel =
    selectedRange && selectedRange[0] && selectedRange[1]
      ? `${dayjs(selectedRange[0]).format("DD/MM/YYYY")} - ${dayjs(
          selectedRange[1]
        ).format("DD/MM/YYYY")}`
      : "No has seleccionado un rango";

  const selectedRangeDaysCount =
    selectedRange && selectedRange[0] && selectedRange[1]
      ? dayjs(selectedRange[1]).diff(dayjs(selectedRange[0]), "day") + 1
      : 0;
  const selectedRangeBlockedCount =
    selectedRange && selectedRange[0] && selectedRange[1]
      ? mandatoryDays.filter((day) => {
          const d = dayjs(day.date);
          return (
            !d.isBefore(dayjs(selectedRange[0]), "day") &&
            !d.isAfter(dayjs(selectedRange[1]), "day")
          );
        }).length
      : 0;

  const renderMandatoryCalendarCell = (current: any) => {
    const yyyymmdd = dayjs(current).format("YYYY-MM-DD");
    const marked = mandatoryDays.some((day) => day.date === yyyymmdd);
    const isToday = dayjs(current).isSame(dayjs(), "day");

    if (!marked) {
      return (
        <div
          className="ant-picker-cell-inner"
          style={{
            borderRadius: 8,
            border: isToday ? "1px dashed #93c5fd" : "1px solid transparent",
            background: isToday ? "#eff6ff" : "transparent",
            color: isToday ? "#1d4ed8" : undefined,
            fontWeight: isToday ? 700 : 500,
            transition: "all .15s ease",
          }}
        >
          {dayjs(current).date()}
        </div>
      );
    }

    return (
      <Popover
        placement="top"
        trigger="click"
        getPopupContainer={(node: any) =>
          (node?.parentElement as HTMLElement) || document.body
        }
        content={
          <div style={{ minWidth: "220px" }}>
            <div className="mb-2">
              <strong>Fecha bloqueada: {yyyymmdd}</strong>
            </div>
            <button
              className="btn btn-danger btn-sm w-100"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRemove(yyyymmdd);
              }}
            >
              Eliminar fecha
            </button>
          </div>
        }
      >
        <div
          className="ant-picker-cell-inner"
          style={{
            borderRadius: 10,
            border: "1px solid #3b82f6",
            fontWeight: 700,
            color: "#1e3a8a",
            background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6)",
          }}
        >
          {dayjs(current).date()}
        </div>
      </Popover>
    );
  };

  return (
    <div>
      <ConfigProvider locale={esES}>
        {!showAssignmentControls ? (
          <div className="card border mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-lg-8">
                  <label className="form-label fw-600 mb-1">1) Selecciona el rango a bloquear</label>
                  <DatePicker.RangePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    value={selectedRange}
                    onChange={(range) => setSelectedRange(range)}
                    getPopupContainer={(triggerNode: any) =>
                      (triggerNode?.parentElement as HTMLElement) || document.body
                    }
                    dateRender={rangeDateRender}
                  />
                  <div className="d-flex flex-wrap align-items-center mt-2" style={{ gap: 8 }}>
                    <span className="badge bg-light text-dark border">
                      Rango: {selectedRangeLabel}
                    </span>
                    <span className="badge bg-primary">
                      {selectedRangeDaysCount} día(s)
                    </span>
                    <span className="badge bg-secondary">
                      {selectedRangeBlockedCount} ya bloqueada(s)
                    </span>
                  </div>
                </div>
                <div className="col-lg-4">
                  <label className="form-label fw-600 mb-1">2) Confirma el bloqueo</label>
                  <button
                    className="btn btn-azul w-100"
                    onClick={handleAdd}
                    disabled={!(selectedRange && selectedRange[0] && selectedRange[1])}
                    style={{ minHeight: 38 }}
                  >
                    Bloquear solo fechas nuevas
                  </button>
                  <small className="text-muted d-block mt-1">
                    Las fechas ya bloqueadas no se vuelven a crear.
                  </small>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="row g-2 mb-3 align-items-end">
            <div className={showAssignmentControls ? "col-md-3" : "col-md-10"}>
              <label className="form-label">Selecciona un rango</label>
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                value={selectedRange}
                onChange={(range) => setSelectedRange(range)}
                getPopupContainer={(triggerNode: any) =>
                  (triggerNode?.parentElement as HTMLElement) || document.body
                }
                dateRender={rangeDateRender}
              />
            </div>
            {showAssignmentControls && (
            <div className="col-md-2">
              <label className="form-label">Sucursal</label>
              <select
                className="form-select"
                value={selectedBranch || ""}
                onChange={(e) => handleBranchChange(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Todas las sucursales</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
            )}
            {showAssignmentControls && (
            <div className="col-md-4">
              <label className="form-label">Colaborador</label>
              <div className="position-relative" ref={searchContainerRef}>
              <div className="input-group">
                <div
                  className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                  style={{ zIndex: 10 }}
                >
                  {searchLoading ? (
                    <i className="fa fa-spinner fa-spin" />
                  ) : (
                    <i className="fa fa-search opacity-5" />
                  )}
                </div>
                <input
                  type="text"
                  className="form-control px-35px bg-light"
                  placeholder={
                    selectAllMode 
                      ? `Todos los colaboradores de la sucursal (${selectedPartners.length})`
                      : selectedPartners.length > 0
                      ? `${selectedPartners.length} colaboradores seleccionados`
                      : selectedPartner
                      ? "Colaborador seleccionado"
                      : "Buscar colaborador..."
                  }
                  value={
                    selectAllMode 
                      ? `Todos los colaboradores de la sucursal (${selectedPartners.length})`
                      : selectedPartners.length > 0
                      ? `${selectedPartners.length} colaboradores seleccionados`
                      : selectedPartner
                      ? partners.find(p => p.id === selectedPartner)?.person.firstName + ' ' + partners.find(p => p.id === selectedPartner)?.person.lastName
                      : searchPartner
                  }
                  onChange={(e) => handlePartnerSearch(e.target.value)}
                  onFocus={() => setShowPartnerDropdown(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setShowPartnerDropdown(false);
                  }}
                />
              </div>
              {showPartnerDropdown && filteredPartners.length > 0 && (
                <div
                  className="position-absolute w-100 bg-white border rounded shadow-sm"
                  style={{
                    zIndex: 1000,
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  {/* Opción "Seleccionar todos" */}
                  {selectedBranch && (
                    <div
                      className="px-3 py-2 border-bottom cursor-pointer hover-bg-light d-flex align-items-center partner-dropdown-item"
                      style={{ 
                        cursor: "pointer",
                        backgroundColor: selectAllMode ? "#e3f2fd" : "white"
                      }}
                      onClick={handleSelectAllPartners}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = selectAllMode ? "#e3f2fd" : "#f8f9fa")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = selectAllMode ? "#e3f2fd" : "white")
                      }
                    >
                      <input
                        type="checkbox"
                        checked={selectAllMode}
                        onChange={() => {}}
                        className="me-2"
                        style={{ width: 16, height: 16 }}
                      />
                      <strong className="text-primary">
                        <i className="fa fa-users me-1"></i>
                        Seleccionar todos los de la sucursal
                      </strong>
                    </div>
                  )}
                  
                  {/* Lista de partners individuales */}
                  {filteredPartners.map((partner) => (
                    <div
                      key={partner.id}
                      className="px-3 py-2 cursor-pointer hover-bg-light d-flex align-items-center partner-dropdown-item"
                      style={{ 
                        cursor: "pointer",
                        backgroundColor: selectedPartners.includes(partner.id) ? "#e3f2fd" : "white"
                      }}
                      onClick={() => handlePartnerToggle(partner.id)}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = selectedPartners.includes(partner.id) ? "#e3f2fd" : "#f8f9fa")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = selectedPartners.includes(partner.id) ? "#e3f2fd" : "white")
                      }
                    >
                      <input
                        type="checkbox"
                        checked={selectedPartners.includes(partner.id)}
                        onChange={() => {}}
                        className="me-2"
                        style={{ width: 16, height: 16 }}
                      />
                      {partner.person.firstName} {partner.person.lastName}
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
            )}
            {showAssignmentControls && (
            <div className="col-md-1">
              <label className="form-label">Bono</label>
              <input
                type="number"
                className="form-control"
                value={bonus}
                onChange={(e) => setBonus(e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
            )}
            <div className={showAssignmentControls ? "col-md-2" : "col-md-2"}>
              <button
                className="btn btn-azul w-100"
                onClick={handleAdd}
                disabled={
                  !(selectedRange && selectedRange[0] && selectedRange[1])
                }
              >
{(() => {
                if (!showAssignmentControls) {
                  return "Crear Fechas";
                }
                const totalSelected = selectAllMode ? selectedPartners.length : (selectedPartner ? 1 : selectedPartners.length);
                if (selectAllMode) {
                  return `Agregar ${selectedPartners.length} Colaboradores`;
                } else if (selectedPartners.length > 0) {
                  return `Agregar ${selectedPartners.length} Colaboradores`;
                } else if (selectedPartner) {
                  return "Agregar Colaborador";
                } else {
                  return "Crear Fechas";
                }
              })()}
              </button>
            </div>
          </div>
        )}

        <div className="row mb-3">
          <div className="col-12">
            <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
              <div
                className="card-header d-flex justify-content-between align-items-center"
                style={{
                  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                  borderBottom: "1px solid #e5e7eb",
                  borderTopLeftRadius: 14,
                  borderTopRightRadius: 14,
                  padding: "14px 16px",
                }}
              >
                <div>
                  <h6 className="mb-0 fw-700">Calendario de temporadas bloqueadas</h6>
                  <small className="text-muted">
                    Clic en una fecha bloqueada para desbloquearla.
                  </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary">{mandatoryDays.length} bloqueadas</span>
                </div>
              </div>
              <div className="card-body" style={{ padding: 16 }}>
                <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 10 }}>
                  <span className="badge bg-light text-dark border d-flex align-items-center gap-2">
                    <Badge color="#3b82f6" />
                    Fecha bloqueada
                  </span>
                  <span className="badge bg-light text-dark border d-flex align-items-center gap-2">
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 9999,
                        background: "#eff6ff",
                        border: "1px dashed #93c5fd",
                        display: "inline-block",
                      }}
                    />
                    Hoy
                  </span>
                </div>
                <Calendar
                  fullscreen={false}
                  value={calendarValue}
                  onPanelChange={(val) => setCalendarValue(val)}
                  onSelect={(val) => setCalendarValue(val)}
                  fullCellRender={renderMandatoryCalendarCell}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Vista por fecha individual removida: ahora solo se usa rango */}

        {showAssignmentControls && selectedRange && selectedRange[0] && selectedRange[1] && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Resumen del rango seleccionado</h6>
                  <div className="d-flex align-items-center">
                    <small className="text-muted me-3">
                      {dayjs(selectedRange[0]).format("DD/MM/YYYY")} -{" "}
                      {dayjs(selectedRange[1]).format("DD/MM/YYYY")}
                    </small>
                    {selectedRowKeys.size > 0 && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setShowBulkDelete(true)}
                      >
                        <i className="fa fa-trash" /> Eliminar seleccionados
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-body">
                  {(() => {
                    const groupedPartners = getGroupedPartners();
                    const start = dayjs(selectedRange[0]);
                    const end = dayjs(selectedRange[1]);
                    
                    // Filtrar colaboradores que estén en el rango seleccionado
                    const partnersInRange = groupedPartners.filter(partner => 
                      partner.dates.some(dateObj => {
                        const date = dayjs(dateObj.date);
                        return !date.isBefore(start, 'day') && !date.isAfter(end, 'day');
                      })
                    );

                    if (partnersInRange.length === 0) {
                      return (
                        <div className="alert alert-info mb-0">
                          No hay colaboradores asignados en el rango seleccionado.
                        </div>
                      );
                    }

                    return (
                      <div
                        className="table-responsive"
                        style={{ maxHeight: "420px", overflowY: "auto" }}
                      >
                        <table
                          className="table table-striped"
                          style={{ minWidth: 600 }}
                        >
                          <thead>
                            <tr>
                              <th style={{ width: 56 }}>
                                <input
                                  type="checkbox"
                                  checked={partnersInRange.length > 0 && selectedRowKeys.size === partnersInRange.length}
                                  ref={(el) => {
                                    if (el && partnersInRange.length > 0) {
                                      el.indeterminate = selectedRowKeys.size > 0 && selectedRowKeys.size < partnersInRange.length;
                                    }
                                  }}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      const allKeys = partnersInRange.map(p => `partner::${p.partnerId}`);
                                      setSelectedRowKeys(new Set(allKeys));
                                    } else {
                                      clearSelection();
                                    }
                                  }}
                                  style={{ width: 20, height: 20 }}
                                />
                              </th>
                              <th>Colaborador</th>
                              <th style={{ width: 120 }}>Días asignados</th>
                              <th style={{ width: 120 }}>Bono total</th>
                              <th style={{ width: 150 }}>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {partnersInRange.map((partner) => {
                              const datesInRange = partner.dates.filter(dateObj => {
                                const date = dayjs(dateObj.date);
                                return !date.isBefore(start, 'day') && !date.isAfter(end, 'day');
                              });
                              
                              const rowKey = `partner::${partner.partnerId}`;
                              return (
                                <tr key={partner.partnerId}>
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedRowKeys.has(rowKey)}
                                      onChange={() => toggleRowSelection(rowKey)}
                                      style={{ width: 20, height: 20 }}
                                    />
                                  </td>
                                  <td>
                                    {partner.partnerName}
                                  </td>
                                  <td>
                                    <span className="badge bg-primary rounded-pill">
                                      {datesInRange.length} días
                                    </span>
                                  </td>
                                  <td>
                                    <span className="badge bg-success rounded-pill">
                                      ${datesInRange.reduce((sum, d) => sum + d.bonus, 0).toFixed(2)}
                                    </span>
                                  </td>
                                  <td>
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => handleShowPartnerDates(partner.partnerId, partner.partnerName)}
                                    >
                                      <i className="fa fa-calendar me-1"></i>
                                      Ver fechas
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        <Modal
          title="Confirmar eliminación"
          open={showBulkDelete}
          onCancel={() => setShowBulkDelete(false)}
          onOk={handleBulkDelete}
          okText="Eliminar"
          cancelText="Cancelar"
          okButtonProps={{ danger: true }}
        >
          <p>
            ¿Deseas eliminar los registros seleccionados? Esta acción no se
            puede deshacer.
          </p>
        </Modal>

        <Modal
          title={`Fechas asignadas - ${selectedPartnerForDates?.partnerName}`}
          open={showPartnerDatesModal}
          onCancel={() => setShowPartnerDatesModal(false)}
          footer={[
            <button
              key="close"
              className="btn btn-secondary"
              onClick={() => setShowPartnerDatesModal(false)}
            >
              Cerrar
            </button>
          ]}
          width={600}
        >
          {selectedPartnerForDates && (
            <div>
              <div className="mb-3">
                <h6>Colaborador: <strong>{selectedPartnerForDates.partnerName}</strong></h6>
                <p className="text-muted mb-0">
                  Total de días asignados: <span className="badge bg-primary">{selectedPartnerForDates.dates.length}</span>
                </p>
              </div>
              
              <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <table className="table table-striped table-sm">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th style={{ width: 100 }}>Bono</th>
                      <th style={{ width: 100 }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPartnerForDates.dates
                      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
                      .map((dateObj, idx) => (
                        <tr key={`${selectedPartnerForDates.partnerId}-${dateObj.date}`}>
                          <td>
                            <span className="badge bg-secondary rounded-pill">
                              {dayjs(dateObj.date).format("DD/MM/YYYY")}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-success rounded-pill">
                              ${dateObj.bonus.toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                handleRemovePartner(dateObj.date, selectedPartnerForDates.partnerId);
                                setShowPartnerDatesModal(false);
                              }}
                              title="Eliminar de esta fecha"
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-3 pt-3 border-top">
                <div className="row">
                  <div className="col-6">
                    <strong>Total de días:</strong> {selectedPartnerForDates.dates.length}
                  </div>
                  <div className="col-6 text-end">
                    <strong>Bono total:</strong> 
                    <span className="badge bg-success ms-2">
                      ${selectedPartnerForDates.dates.reduce((sum, d) => sum + d.bonus, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </ConfigProvider>

      <div className="d-flex justify-content-end">
        <button className="btn btn-secondary" onClick={closeFn}>
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default EditMandatoryDays;
